import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import "primeicons/primeicons.css";
// import "./CampaignAssignmentFlow.css";
import "./ReportingHierarchy.css";
import { IoArrowBack } from "react-icons/io5";
import Loader from "../loader/loader";
import ToastrService from "../../services/toastService";
import { Logout, shouldLogout } from "../Common/Common";
import { useGetCampaignAssignmentFlowQuery } from "./Api";

/* =========================================================
   HELPERS
========================================================= */

const getInitials = (name) => {
    if (!name) return "";

    return String(name)
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word.charAt(0))
        .join("")
        .substring(0, 2)
        .toUpperCase();
};

const addHexOpacity = (hex, opacity) => {
    if (!hex) {
        return "#7180961A";
    }

    let value = String(hex).replace("#", "");

    if (value.length === 3) {
        value = value
            .split("")
            .map((x) => x + x)
            .join("");
    }

    const alpha = Math.round(opacity * 255)
        .toString(16)
        .padStart(2, "0");

    return `#${value}${alpha}`;
};

const formatRoutingStrategy = (value) => {
    if (!value) return "";

    return String(value)
        .split("_")
        .map(
            (word) =>
                word.charAt(0).toUpperCase() +
                word.slice(1)
        )
        .join(" ");
};

/* =========================================================
   PROFILE AVATAR
========================================================= */

const ProfileAvatar = ({
    name,
    image,
    className = "",
    alt = "profile",
}) => {
    const [imageError, setImageError] = useState(false);

    useEffect(() => {
        setImageError(false);
    }, [image]);

    const hasImage =
        image &&
        typeof image === "string" &&
        image.trim() !== "" &&
        !imageError;

    if (hasImage) {
        return (
            <img
                src={image}
                alt={alt || name || "profile"}
                className={`caf-profile-avatar ${className}`}
                onError={() => setImageError(true)}
            />
        );
    }

    return (
        <div
            className={`caf-profile-avatar caf-profile-avatar-text ${className}`}
            title={name || ""}
        >
            {getInitials(name)}
        </div>
    );
};

/* =========================================================
   AGENT CARD
========================================================= */

const AgentCard = ({ agent }) => {
    const statusColor =
        agent?.callStatusColour || "#718096";

    const statusBackground =
        addHexOpacity(statusColor, 0.1);

    return (
        <div className="caf-agent">

            <ProfileAvatar
                name={agent?.name}
                image={agent?.image}
                className="caf-agent-image"
                alt={agent?.name}
            />

            <div className="caf-agent-details">

                <div className="caf-agent-id">
                    Agent{" "}
                    {agent?.flexzId ||
                        agent?.agentId ||
                        "-"}
                </div>

                <div className="caf-agent-name">
                    {agent?.name || "-"}
                </div>

            </div>

            <span
                className="caf-status"
                style={{
                    color: statusColor,
                    backgroundColor: statusBackground,
                }}
            >
                <span
                    className="caf-status-dot"
                    style={{
                        backgroundColor: statusColor,
                    }}
                />

                {agent?.callStatus || "Unknown"}
            </span>

        </div>
    );
};

/* =========================================================
   CAMPAIGN CARD
========================================================= */

const CampaignCard = ({ campaign }) => {
    return (
        <div className="caf-campaign">

            <div className="caf-campaign-icon">
                <i className="pi pi-megaphone" />
            </div>

            <div className="caf-campaign-info">

                <div className="caf-label">
                    Campaign
                </div>

                <div className="caf-title">
                    {campaign?.name || "-"}
                </div>

                <div className="caf-code">
                    {campaign?.code || "-"}
                </div>

                <div className="caf-channel">
                    Channel:{" "}
                    {campaign?.channel
                        ? campaign.channel
                            .charAt(0)
                            .toUpperCase() +
                        campaign.channel.slice(1)
                        : "-"}
                </div>

            </div>

        </div>
    );
};

/* =========================================================
   MANAGER CARD
========================================================= */

const ManagerCard = ({
    manager,
    expanded,
    onToggle,
}) => {
    return (
        <div className="caf-manager">

            <ProfileAvatar
                name={manager?.name}
                image={manager?.image}
                className="caf-manager-image"
                alt={manager?.name}
            />

            <div className="caf-manager-info">

                <div className="caf-label">
                    Manager
                </div>

                <div className="caf-manager-name">
                    {manager?.name || "-"}
                </div>

                <div className="caf-code">
                    {manager?.flexzId ||
                        manager?.employeeId ||
                        "-"}
                </div>

            </div>

            <button
                type="button"
                className="caf-toggle"
                onClick={onToggle}
                aria-label={
                    expanded
                        ? "Collapse manager"
                        : "Expand manager"
                }
            >
                <i
                    className={
                        expanded
                            ? "pi pi-minus"
                            : "pi pi-plus"
                    }
                />
            </button>

        </div>
    );
};

/* =========================================================
   QUEUE CARD
========================================================= */

const QueueCard = ({
    queue,
    expanded,
    onToggle,
}) => {

    const supervisors =
        Array.isArray(queue?.supervisors)
            ? queue.supervisors
            : [];

    const supervisorCount =
        queue?.supervisorCount ??
        supervisors.length;

    const agentCount =
        queue?.agentCount ??
        supervisors.reduce(
            (total, supervisor) =>
                total +
                (Array.isArray(supervisor?.agents)
                    ? supervisor.agents.length
                    : 0),
            0
        );

    return (
        <div className="caf-queue">

            <div className="caf-queue-icon">
                <i className="pi pi-users" />
            </div>

            <div className="caf-queue-info">

                <div className="caf-label">
                    Routing Queue
                </div>

                <div
                    className="caf-title"
                    title={`${queue?.code || "-"} - ${queue?.name || "-"}`}
                >
                    {queue?.code || "-"} -{" "}
                    {queue?.name || "-"}
                </div>

                <div className="caf-queue-meta">

                    <span>
                        Priority{" "}
                        {queue?.priority ?? "-"}
                    </span>

                    <span className="caf-meta-dot">
                        •
                    </span>

                    <span>
                        {formatRoutingStrategy(
                            queue?.routingStrategy
                        )}
                    </span>

                </div>

                <div className="caf-queue-stats">

                    <span className="caf-queue-stat">

                        <i className="pi pi-users" />

                        <strong>
                            {supervisorCount}
                        </strong>

                        <span>
                            Supervisors
                        </span>

                    </span>

                    <span className="caf-queue-stat-divider">
                        •
                    </span>

                    <span className="caf-queue-stat">

                        <i className="pi pi-user" />

                        <strong>
                            {agentCount}
                        </strong>

                        <span>
                            Agents
                        </span>

                    </span>

                </div>

            </div>

            <button
                type="button"
                className="caf-toggle"
                onClick={onToggle}
                aria-label={
                    expanded
                        ? "Collapse routing queue"
                        : "Expand routing queue"
                }
            >
                <i
                    className={
                        expanded
                            ? "pi pi-minus"
                            : "pi pi-plus"
                    }
                />
            </button>

        </div>
    );
};

/* =========================================================
   AGENT SEARCH
========================================================= */

const AgentSearch = ({
    searchTerm,
    setSearchTerm,
}) => {
    return (
        <div className="caf-agent-search">

            <i className="pi pi-search" />

            <input
                type="text"
                value={searchTerm}
                onChange={(e) =>
                    setSearchTerm(e.target.value)
                }
                placeholder="Search agents..."
                aria-label="Search agents"
            />

            {searchTerm && (
                <button
                    type="button"
                    className="caf-agent-search-clear"
                    onClick={() =>
                        setSearchTerm("")
                    }
                    aria-label="Clear agent search"
                >
                    <i className="pi pi-times" />
                </button>
            )}

        </div>
    );
};

/* =========================================================
   SUPERVISOR CARD
========================================================= */

const SupervisorCard = ({
    supervisor,
    expanded,
    onToggle,
}) => {

    const [searchTerm, setSearchTerm] =
        useState("");

    const agents =
        Array.isArray(supervisor?.agents)
            ? supervisor.agents
            : [];

    const filteredAgents = useMemo(() => {

        const search =
            searchTerm.trim().toLowerCase();

        if (!search) {
            return agents;
        }

        return agents.filter((agent) => {

            const name =
                String(
                    agent?.name || ""
                ).toLowerCase();

            const flexzId =
                String(
                    agent?.flexzId || ""
                ).toLowerCase();

            const agentId =
                String(
                    agent?.agentId || ""
                ).toLowerCase();

            const status =
                String(
                    agent?.status ||
                    agent?.callStatus ||
                    ""
                ).toLowerCase();

            return (
                name.includes(search) ||
                flexzId.includes(search) ||
                agentId.includes(search) ||
                status.includes(search)
            );
        });

    }, [agents, searchTerm]);

    return (
        <div className="caf-supervisor-column">

            <div className="caf-supervisor">

                <ProfileAvatar
                    name={supervisor?.name}
                    image={supervisor?.image}
                    className="caf-supervisor-image"
                    alt={supervisor?.name}
                />

                <div className="caf-supervisor-info">

                    <div className="caf-label">
                        Supervisor
                    </div>

                    <div
                        className="caf-supervisor-name"
                        title={supervisor?.name || ""}
                    >
                        {supervisor?.name || "-"}
                    </div>

                    <div className="caf-code">
                        {supervisor?.supervisorId ||
                            supervisor?.employeeId ||
                            "-"}
                    </div>

                </div>

                <button
                    type="button"
                    className="caf-toggle"
                    onClick={onToggle}
                    aria-label={
                        expanded
                            ? `Collapse ${supervisor?.name || "supervisor"}`
                            : `Expand ${supervisor?.name || "supervisor"}`
                    }
                >
                    <i
                        className={
                            expanded
                                ? "pi pi-minus"
                                : "pi pi-plus"
                        }
                    />
                </button>

            </div>

            {expanded && (
                <>

                    <div className="caf-supervisor-agent-connector">
                        <div className="caf-supervisor-agent-line" />
                    </div>

                    <div className="caf-agent-container">

                        {agents.length > 0 && (
                            <AgentSearch
                                searchTerm={searchTerm}
                                setSearchTerm={
                                    setSearchTerm
                                }
                            />
                        )}

                        {agents.length === 0 ? (

                            <div className="caf-empty">

                                <span className="caf-no-users-icon">

                                    <i className="pi pi-users" />

                                    <span className="caf-no-users-strike" />

                                </span>

                                <span>
                                    No agents assigned
                                </span>

                            </div>

                        ) : filteredAgents.length === 0 ? (

                            <div className="caf-agent-no-results">

                                <i className="pi pi-search" />

                                <span>
                                    No agents found
                                </span>

                            </div>

                        ) : (

                            <div className="caf-agent-scroll">

                                {filteredAgents.map(
                                    (agent, index) => (
                                        <AgentCard
                                            key={
                                                agent?.id ||
                                                agent?.agentId ||
                                                index
                                            }
                                            agent={agent}
                                        />
                                    )
                                )}

                            </div>

                        )}

                        {agents.length > 0 && (
                            <div className="caf-agent-footer">

                                <span>
                                    Total Agents
                                </span>

                                <strong>
                                    {searchTerm
                                        ? `${filteredAgents.length} / ${agents.length}`
                                        : agents.length}
                                </strong>

                            </div>
                        )}

                    </div>

                </>
            )}

        </div>
    );
};

/* =========================================================
   SUPERVISOR TREE CONNECTOR
========================================================= */

const SupervisorConnector = ({ count }) => {

    if (!count) {
        return null;
    }

    return (
        <div
            className={`caf-supervisor-tree-connector caf-supervisor-tree-connector-${count}`}
            style={{
                "--caf-supervisor-count": count,
            }}
            aria-hidden="true"
        >

            <div className="caf-supervisor-tree-horizontal" />

            {Array.from({
                length: count,
            }).map((_, index) => (
                <div
                    key={index}
                    className="caf-supervisor-tree-drop"
                />
            ))}

        </div>
    );
};

/* =========================================================
   QUEUE -> SUPERVISORS
========================================================= */

const QueueSupervisors = ({
    queue,
    queueIndex,
    expandedKeys,
    toggleNode,
}) => {

    const supervisors =
        Array.isArray(queue?.supervisors)
            ? queue.supervisors
            : [];

    // Render all supervisors. The supervisor container can be
    // horizontally scrolled by the user; no left/right navigation
    // buttons are required.
    const visibleSupervisors = supervisors;

    /* =====================================================
       NO SUPERVISORS
    ===================================================== */

    if (supervisors.length === 0) {

        return (
            <div className="caf-supervisor-empty-wrapper">

                <div className="caf-supervisor-empty-line" />

                <div className="caf-no-supervisors">

                    <i className="pi pi-users" />

                    <span>
                        No supervisors assigned
                        to this queue.
                    </span>

                </div>

            </div>
        );
    }

    return (
        <div className="caf-supervisor-section">

            <div
                className="caf-queue-supervisor-incoming"
                aria-hidden="true"
            >
                <div className="caf-queue-supervisor-incoming-line" />
            </div>

            <div className="caf-supervisor-tree-wrapper">

                <div className="caf-supervisor-content">

                    <SupervisorConnector
                        count={
                            visibleSupervisors.length
                        }
                    />

                    <div className="caf-supervisor-row">

                        {visibleSupervisors.map(
                            (
                                supervisor,
                                supervisorIndex
                            ) => {

                                const actualIndex = supervisorIndex;

                                const supervisorKey =
                                    supervisor?.key ||
                                    `supervisor-${queue?.id || queueIndex}-${supervisor?.id || actualIndex}`;

                                return (
                                    <SupervisorCard
                                        key={
                                            supervisorKey
                                        }
                                        supervisor={
                                            supervisor
                                        }
                                        expanded={
                                            !!expandedKeys[
                                            supervisorKey
                                            ]
                                        }
                                        onToggle={() =>
                                            toggleNode(
                                                supervisorKey
                                            )
                                        }
                                    />
                                );
                            }
                        )}

                    </div>

                </div>

            </div>

        </div>
    );
};

/* =========================================================
   VERTICAL CONNECTOR
========================================================= */

const VerticalConnector = () => {
    return (
        <div
            className="caf-manager-queue-connector"
            aria-hidden="true"
        >
            <div className="caf-manager-queue-connector-line" />
        </div>
    );
};
const getQueueVisibleCount = () => {
    if (typeof window === "undefined") {
        return 1;
    }

    // Large devices -> 2 queues
    // Smaller devices -> 1 queue
    return window.innerWidth >= 1200 ? 2 : 1;
};
/* =========================================================
   MAIN COMPONENT
========================================================= */


/* =========================================================
   FIGMA-STYLE HAND DRAG
   ========================================================= */

const useHandDrag = () => {
    const ref = useRef(null);
    const dragState = useRef({
        active: false,
        startX: 0,
        startY: 0,
        scrollLeft: 0,
        scrollTop: 0,
    });

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        const onPointerDown = (event) => {
            if (event.pointerType === "mouse" && event.button !== 0) return;

            if (
                event.target.closest(
                    "button, input, textarea, select, a, [role='button']"
                )
            ) {
                return;
            }

            dragState.current = {
                active: true,
                startX: event.clientX,
                startY: event.clientY,
                scrollLeft: element.scrollLeft,
                scrollTop: element.scrollTop,
            };

            element.classList.add("caf-hand-dragging");
            element.setPointerCapture?.(event.pointerId);
        };

        const onPointerMove = (event) => {
            if (!dragState.current.active) return;

            element.scrollLeft =
                dragState.current.scrollLeft -
                (event.clientX - dragState.current.startX);

            element.scrollTop =
                dragState.current.scrollTop -
                (event.clientY - dragState.current.startY);
        };

        const stopDragging = (event) => {
            dragState.current.active = false;
            element.classList.remove("caf-hand-dragging");

            if (
                event?.pointerId !== undefined &&
                element.hasPointerCapture?.(event.pointerId)
            ) {
                element.releasePointerCapture(event.pointerId);
            }
        };

        element.addEventListener("pointerdown", onPointerDown);
        element.addEventListener("pointermove", onPointerMove);
        element.addEventListener("pointerup", stopDragging);
        element.addEventListener("pointercancel", stopDragging);
        element.addEventListener("lostpointercapture", stopDragging);

        return () => {
            element.removeEventListener("pointerdown", onPointerDown);
            element.removeEventListener("pointermove", onPointerMove);
            element.removeEventListener("pointerup", stopDragging);
            element.removeEventListener("pointercancel", stopDragging);
            element.removeEventListener("lostpointercapture", stopDragging);
        };
    }, []);

    return ref;
};

const ReportingHierarchy = () => {

    const flowDragRef = useHandDrag();

    const navigate = useNavigate();
    const location = useLocation();

    /* =====================================================
       CAMPAIGN ID
    ===================================================== */

    const campaignId =
        location.state?.campaignId;

    /* =====================================================
       API
    ===================================================== */

    const {
        data: apiResponse,
        isLoading,
        error,
    } = useGetCampaignAssignmentFlowQuery(
        campaignId,
        {
            skip: !campaignId,
        }
    );

    /* =====================================================
       API DATA
    ===================================================== */

    const assignmentData =
        apiResponse?.data;

    const campaign =
        assignmentData?.campaign;

    const manager =
        assignmentData?.manager;

    /* =====================================================
       QUEUES
    ===================================================== */

    const queues = useMemo(() => {

        if (
            Array.isArray(
                assignmentData?.queues
            )
        ) {
            return assignmentData.queues;
        }

        if (assignmentData?.queue) {

            return [
                {
                    ...assignmentData.queue,
                    supervisors:
                        assignmentData?.supervisors ||
                        [],
                },
            ];
        }

        return [];

    }, [assignmentData]);

    /* =====================================================
       EXPANDED STATE
    ===================================================== */

    const [expandedKeys, setExpandedKeys] =
        useState({
            campaign: true,
            manager: true,
        });

    /* =====================================================
       QUEUE DISPLAY COUNT

       Large devices -> 2 queues visible at a time
       Smaller devices -> 1 queue visible at a time

       All queues remain rendered. CSS controls the queue width,
       while the user can horizontally scroll the queue container.
    ===================================================== */

    const [queuesVisible, setQueuesVisible] = useState(
        getQueueVisibleCount()
    );

    useEffect(() => {
        const handleResize = () => {
            setQueuesVisible(getQueueVisibleCount());
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const QUEUES_VISIBLE = queuesVisible;

    // Do not slice the queues. Render all queues and let the
    // horizontal container handle scrolling.
    const visibleQueues = queues;

    /* =====================================================
       INITIALIZE EXPANDED KEYS
    ===================================================== */

    useEffect(() => {

        if (!queues.length) {
            return;
        }

        setExpandedKeys((previous) => {

            const updated = {
                ...previous,
            };

            queues.forEach(
                (
                    queue,
                    queueIndex
                ) => {

                    const queueKey =
                        queue?.key ||
                        `queue-${queue?.id || queueIndex}`;

                    if (
                        updated[
                        queueKey
                        ] === undefined
                    ) {
                        updated[
                            queueKey
                        ] = true;
                    }

                    const supervisors =
                        Array.isArray(
                            queue?.supervisors
                        )
                            ? queue.supervisors
                            : [];

                    supervisors.forEach(
                        (
                            supervisor,
                            supervisorIndex
                        ) => {

                            const supervisorKey =
                                supervisor?.key ||
                                `supervisor-${queue?.id || queueIndex}-${supervisor?.id || supervisorIndex}`;

                            if (
                                updated[
                                supervisorKey
                                ] === undefined
                            ) {
                                updated[
                                    supervisorKey
                                ] = true;
                            }

                        }
                    );

                }
            );

            return updated;
        });

    }, [queues]);

    /* =====================================================
       TOGGLE
    ===================================================== */

    const toggleNode = (key) => {

        setExpandedKeys((previous) => ({
            ...previous,
            [key]: !previous[key],
        }));

    };

    /* =====================================================
       EXPAND ALL
    ===================================================== */

    const expandAll = () => {

        const expanded = {
            campaign: true,
            manager: true,
        };

        queues.forEach(
            (
                queue,
                queueIndex
            ) => {

                const queueKey =
                    queue?.key ||
                    `queue-${queue?.id || queueIndex}`;

                expanded[queueKey] = true;

                const supervisors =
                    Array.isArray(
                        queue?.supervisors
                    )
                        ? queue.supervisors
                        : [];

                supervisors.forEach(
                    (
                        supervisor,
                        supervisorIndex
                    ) => {

                        const supervisorKey =
                            supervisor?.key ||
                            `supervisor-${queue?.id || queueIndex}-${supervisor?.id || supervisorIndex}`;

                        expanded[
                            supervisorKey
                        ] = true;

                    }
                );

            }
        );

        setExpandedKeys(expanded);
    };

    /* =====================================================
       COLLAPSE ALL
    ===================================================== */

    const collapseAll = () => {

        const collapsed = {
            campaign: false,
            manager: false,
        };

        queues.forEach(
            (
                queue,
                queueIndex
            ) => {

                const queueKey =
                    queue?.key ||
                    `queue-${queue?.id || queueIndex}`;

                collapsed[
                    queueKey
                ] = false;

                const supervisors =
                    Array.isArray(
                        queue?.supervisors
                    )
                        ? queue.supervisors
                        : [];

                supervisors.forEach(
                    (
                        supervisor,
                        supervisorIndex
                    ) => {

                        const supervisorKey =
                            supervisor?.key ||
                            `supervisor-${queue?.id || queueIndex}-${supervisor?.id || supervisorIndex}`;

                        collapsed[
                            supervisorKey
                        ] = false;

                    }
                );

            }
        );

        setExpandedKeys(collapsed);
    };
    const isAllExpanded = useMemo(() => {
        if (!queues.length) {
            return false;
        }

        // Campaign and manager must also be expanded.
        if (
            !expandedKeys.campaign ||
            !expandedKeys.manager
        ) {
            return false;
        }

        for (const queue of queues) {
            const queueKey =
                queue?.key ||
                `queue-${queue?.id || queues.indexOf(queue)}`;

            if (!expandedKeys[queueKey]) {
                return false;
            }

            const supervisors = Array.isArray(
                queue?.supervisors
            )
                ? queue.supervisors
                : [];

            for (
                let supervisorIndex = 0;
                supervisorIndex < supervisors.length;
                supervisorIndex++
            ) {
                const supervisor =
                    supervisors[supervisorIndex];

                const supervisorKey =
                    supervisor?.key ||
                    `supervisor-${queue?.id || queues.indexOf(queue)}-${supervisor?.id || supervisorIndex}`;

                if (!expandedKeys[supervisorKey]) {
                    return false;
                }
            }
        }

        return true;
    }, [queues, expandedKeys]);
    /* =====================================================
       ERROR
    ===================================================== */

    useEffect(() => {

        if (!error) {
            return;
        }

        if (shouldLogout(error)) {
            Logout(navigate);
            return;
        }

        const message =
            error?.data?.message ||
            "Unable to load campaign assignment flow.";

        ToastrService.error(message);

    }, [error, navigate]);

    /* =====================================================
       LOADING
    ===================================================== */

    if (isLoading) {
        return <Loader />;
    }

    /* =====================================================
       NO DATA
    ===================================================== */

    if (!assignmentData) {

        return (
            <div className="caf-page">

                <div className="main-content">

                    <div className="p-4">

                        <div className="alert alert-warning">
                            No campaign assignment data found.
                        </div>

                    </div>

                </div>

            </div>
        );
    }

    /* =====================================================
       UI
    ===================================================== */

    return (
        <div className="caf-page">

            {/* =================================================
                HEADER
            ================================================= */}

            <div className="page-header">

                <div className="page-header-left d-flex align-items-center">

                    <ul className="breadcrumb mb-0">

                        <li className="breadcrumb-item">
                            Campaigns
                        </li>

                        <li className="breadcrumb-item active">
                            Hierarchy
                        </li>

                    </ul>

                </div>

            </div>

            {/* =================================================
                CONTENT
            ================================================= */}

            <div className="main-content">

                <div className="row">

                    <div className="p-6 caf-content-wrapper">

                        {/* =================================================
                            TITLE / ACTIONS
                        ================================================= */}

                        <div className="row align-items-center g-3 mb-3">

                            {/* Left Section */}
                            <div className="col-12 col-md">
                                <div className="d-flex align-items-center gap-3">

                                    {/* Back Button */}
                                    <button
                                        type="button"
                                        onClick={() => navigate("/campaign/agent-assignment")}
                                        className="back-btn border-0 d-flex align-items-center justify-content-center flex-shrink-0"
                                    >
                                        <IoArrowBack size={16} />
                                    </button>

                                    {/* Heading */}
                                    <div className="min-w-0">
                                        <h3 className="h4 mb-1 drawer-header">
                                            Campaign-Based Assignment Flow
                                        </h3>

                                        <p className="mb-0 text-muted">
                                            View campaign, manager, queues, supervisors, and assigned agents.
                                        </p>
                                    </div>

                                </div>
                            </div>

                            {/* Right Section */}
                            <div className="col-12 col-md-auto">
                                <div className="d-flex justify-content-start justify-content-md-end">
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => {
                                            if (isAllExpanded) {
                                                collapseAll();
                                            } else {
                                                expandAll();
                                            }
                                        }}
                                    >
                                        <i
                                            className={`pi ${isAllExpanded
                                                ? "pi-minus-circle"
                                                : "pi-plus-circle"
                                                } me-1`}
                                        />

                                        {isAllExpanded ? "Collapse All" : "Expand All"}
                                    </button>
                                </div>
                            </div>

                        </div>

                        {/* =================================================
                            MAIN CARD
                        ================================================= */}

                        <div className="caf-flow-card" ref={flowDragRef}>

                            {/* =================================================
                                CAMPAIGN + MANAGER
                            ================================================= */}

                            <div className="caf-campaign-manager-area">

                                <div className="caf-campaign-manager-row">

                                    <div className="caf-campaign-node">

                                        <CampaignCard
                                            campaign={
                                                campaign
                                            }
                                        />

                                    </div>

                                    {expandedKeys.campaign && (
                                        <div className="caf-campaign-manager-connector">
                                            <div className="caf-horizontal-connector-line" />
                                        </div>
                                    )}

                                    {expandedKeys.campaign && (
                                        <div className="caf-manager-node">

                                            <ManagerCard
                                                manager={
                                                    manager
                                                }
                                                expanded={
                                                    !!expandedKeys.manager
                                                }
                                                onToggle={() =>
                                                    toggleNode(
                                                        "manager"
                                                    )
                                                }
                                            />

                                        </div>
                                    )}

                                </div>

                            </div>

                            {/* =================================================
                                MANAGER -> QUEUES
                            ================================================= */}

                            {expandedKeys.campaign &&
                                expandedKeys.manager &&
                                queues.length > 0 && (
                                    <>

                                        <VerticalConnector />

                                        {/* <div className="caf-queue-section"> */}
                                        <div
                                            className={`caf-queue-section caf-queue-count-${QUEUES_VISIBLE}`}
                                        >

                                            <div className="caf-queue-viewport">

                                                <div className="caf-queue-row">

                                                    {visibleQueues.map(
                                                        (
                                                            queue,
                                                            queueIndex
                                                        ) => {

                                                            const actualQueueIndex = queueIndex;

                                                            const queueKey =
                                                                queue?.key ||
                                                                `queue-${queue?.id || actualQueueIndex}`;

                                                            return (
                                                                <div
                                                                    className="caf-queue-column"
                                                                    key={
                                                                        queueKey
                                                                    }
                                                                >

                                                                    <QueueCard
                                                                        queue={
                                                                            queue
                                                                        }
                                                                        expanded={
                                                                            !!expandedKeys[
                                                                            queueKey
                                                                            ]
                                                                        }
                                                                        onToggle={() =>
                                                                            toggleNode(
                                                                                queueKey
                                                                            )
                                                                        }
                                                                    />

                                                                    {expandedKeys[
                                                                        queueKey
                                                                    ] && (
                                                                            <QueueSupervisors
                                                                                queue={
                                                                                    queue
                                                                                }
                                                                                queueIndex={
                                                                                    actualQueueIndex
                                                                                }
                                                                                expandedKeys={
                                                                                    expandedKeys
                                                                                }
                                                                                toggleNode={
                                                                                    toggleNode
                                                                                }
                                                                            />
                                                                        )}

                                                                </div>
                                                            );
                                                        }
                                                    )}

                                                </div>

                                            </div>

                                        </div>

                                    </>
                                )}

                            {/* =================================================
                                NO QUEUES
                            ================================================= */}

                            {expandedKeys.campaign &&
                                expandedKeys.manager &&
                                queues.length === 0 && (

                                    <div className="caf-no-queues">

                                        <i className="pi pi-users" />

                                        <span>
                                            No queues assigned
                                            to this manager.
                                        </span>

                                    </div>

                                )}

                        </div>

                    </div>

                </div>

            </div>

        </div>
    );
};

export default ReportingHierarchy;